<template>
  <div>
    <global-confirm-box ref="confirmBox" />
    <v-row align="end" justify="space-between" class="mt-4 mb-0">
      <v-col cols="auto">
        <span class="headline black--text">{{ title }}</span>
      </v-col>
      <v-col>
        <v-row no-gutters align="center" justify="end">
          <text-field
            v-model="search"
            :placeholder="$t('search')"
            hide-details
            kind="search"
          />
        </v-row>
      </v-col>
    </v-row>
    <warehouse-grocery-placeholder
      v-if="warehouse && !disabled"
      :warehouse="warehouse"
      :type="placeholderType"
      :existing-groceries="groceries"
      @groceryAdded="addGrocery"
    />
    <v-data-table
      :headers="headers"
      :items="filteredGroceries"
      item-key="id"
      class="striped-table ordera-box-shadow"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :footer-props="{
        'items-per-page-options': [5, 10, 25, 50, -1],
        'show-current-page': true,
        'show-first-last-page': true,
      }"
      :loading="loading.addGrocery || loading.removeGrocery"
    >
      <template v-slot:item.num="{ index }">
        <td>
          {{ (page - 1) * itemsPerPage + index + 1 }}
        </td>
      </template>

      <template v-slot:item.supplier_code="{ item }">
        <text-field
          v-model="item.supplier_code"
          :disabled="disabled"
          hide-details
          dense
          color="white"
        />
      </template>

      <!-- TODO: gross_quantity slot - front-end only until backend model is added -->
      <template v-slot:item.gross_quantity="{ item }">
        <v-row no-gutters justify="end">
          <div style="width: 110px; max-width: 100%">
            <text-field
              :value="Number(item.gross_quantity) ? item.gross_quantity : ''"
              @input="item.gross_quantity = $event !== '' ? Number($event) : 0"
              type="number"
              :min="item.amount || 0"
              :disabled="disabled"
              step="0.01"
              hide-details
              dense
              color="white"
            />
          </div>
        </v-row>
      </template>

      <template v-slot:item.amount="{ item }">
        <v-row no-gutters justify="end">
          <div style="width: 110px; max-width: 100%">
            <text-field
              v-model.number="item.amount"
              type="number"
              :max="
                hasWarehouseStock(item)
                  ? item.stock || Number(item.warehouse_stock)
                  : undefined
              "
              min="0"
              :disabled="disabled"
              step="0.01"
              hide-details
              dense
              color="white"
            />
          </div>
        </v-row>
      </template>

      <!-- TODO: unit_price slot - front-end only until backend model is added -->
      <template v-slot:item.unit_price="{ item }">
        <v-row no-gutters justify="end">
          <div style="width: 110px; max-width: 100%">
            <text-field
              :value="Number(item.unit_price) ? item.unit_price : ''"
              @input="item.unit_price = $event !== '' ? Number($event) : 0"
              type="number"
              min="0"
              :disabled="disabled"
              step="0.01"
              hide-details
              dense
              :suffix="restaurantCurrency"
              color="white"
            />
          </div>
        </v-row>
      </template>

      <!-- ukupno = kolicina (neto) * unit_price, read-only -->
      <template v-slot:item.total_amount="{ item }">
        <v-row no-gutters justify="end">
          <span>
            {{ calcAmount(item) }}
          </span>
        </v-row>
      </template>

      <template v-slot:item.warehouse_stock="{ item }">
        {{ item.warehouse_stock || item.stockWithMeasure }}
      </template>

      <template v-slot:item.actions="{ item }" v-if="!disabled">
        <remove-button @click="removeGrocery(item)" :disabled="loading.removeGrocery" />
      </template>
    </v-data-table>
  </div>
</template>

<script>
import WarehouseGroceryPlaceholder from "@/components/warehouse/shared/WarehouseGroceryPlaceholder.vue";
import warehouseService from "@/services/warehouse";

export default {
  props: {
    groceries: {
      type: Array,
      required: true,
    },
    warehouse: {
      type: [Object, null, undefined],
      required: true,
    },
    headers: {
      type: Array,
      required: true,
    },
    waybill: {
      type: Boolean,
      default: false,
    },
    entry: {
      type: Boolean,
      default: false,
    },
    writeOff: {
      type: Boolean,
      default: false,
    },
    return: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    WarehouseGroceryPlaceholder,
  },
  data: () => ({
    search: "",
    page: 1,
    itemsPerPage: 10,
    warehouseStatus: null,
    loading: {
      addGrocery: false,
      removeGrocery: false,
      warehouseStatus: false,
    },
  }),
  methods: {
    async fetchWarehouseStatus() {
      if (!this.warehouse?.id) return;

      this.loading.warehouseStatus = true;
      try {
        const response = await warehouseService.getWarehouseStatus({
          warehouse_ids: this.warehouse.id,
          date: new Date().toISOString().split("T")[0],
        });

        this.warehouseStatus = response.data;
        this.updateGroceriesWithStock();
      } catch (error) {
        console.error("Error fetching warehouse status:", error);
      } finally {
        this.loading.warehouseStatus = false;
      }
    },
    updateGroceriesWithStock() {
      if (!this.warehouseStatus || !this.warehouseStatus.length) return;

      const warehouseData = this.warehouseStatus[0];
      if (!warehouseData?.groceries) return;

      // Create a map for quick lookup
      const stockMap = new Map(warehouseData.groceries.map((g) => [g.id, g.total]));

      // Update groceries with stock information
      this.groceries.forEach((grocery) => {
        const stock = stockMap.get(grocery.id);
        if (stock !== undefined) {
          this.$set(grocery, "warehouse_stock", stock);
          this.$set(grocery, "stockWithMeasure", `${stock} ${grocery.measure}`);
        }
      });
    },
    hasWarehouseStock(item) {
      return (
        (this.waybill || this.writeOff || this.return) &&
        item.warehouse_stock !== undefined
      );
    },
    calcAmount(item) {
      const amount = Number(item.amount);
      const unitPrice = Number(item.unit_price);
      if (!amount || !unitPrice) return "";
      return `${(amount * unitPrice).toFixed(2)} ${this.restaurantCurrency}`;
    },
    addGrocery(grocery) {
      if (this.loading.addGrocery) return;

      this.loading.addGrocery = true;

      try {
        const orderGrocery = this.groceries.find((item) => item.id === grocery.id);
        if (orderGrocery) {
          orderGrocery.quantity = grocery.quantity;
          return;
        }
        // TODO: initialize gross_quatity and unit_price once backend models are added
        if (grocery.gross_quantity === undefined)
          this.$set(grocery, "gross_quantity", null);
        if (grocery.unit_price === undefined) this.$set(grocery, "unit_price", null);
        if (grocery.supplier_code === undefined) this.$set(grocery, "supplier_code", "");
        this.groceries.unshift(grocery);
        this.page = 1; // Reset to first page when adding new item
      } catch (error) {
        console.error("Error adding grocery:", error);
      } finally {
        this.loading.addGrocery = false;
      }
    },
    removeGrocery(item) {
      if (this.loading.removeGrocery) return;
      this.loading.removeGrocery = true;

      this.$refs.confirmBox
        .open(
          this.$t("warehouse.delete_grocery"),
          this.$t("warehouse.confirm_delete_grocery"),
          {
            color: "warning",
          }
        )
        .then(() => {
          this.$emit("removeGrocery", item);
        })
        .catch((err) => console.warn(err))
        .finally(() => {
          this.loading.removeGrocery = false;
        });
    },
  },
  computed: {
    title() {
      return this.waybill
        ? this.$t("goods_for_issuance")
        : this.writeOff
        ? this.$t("written_off_goods")
        : this.return
        ? this.$t("returned_goods")
        : this.entry
        ? this.$t("received_goods")
        : this.$t("ordered_goods");
    },
    placeholderType() {
      if (this.waybill) return "waybill";
      if (this.writeOff) return "writeoff";
      if (this.return) return "return";
      if (this.entry) return "entry";
      return "purchase";
    },
    isExitType() {
      return this.waybill || this.writeOff || this.return;
    },
    filteredGroceries() {
      if (!this.search) return this.groceries;
      const searchLower = this.search.toLowerCase();
      return this.groceries.filter((item) => {
        return (
          item.name?.toLowerCase().includes(searchLower) ||
          item.code?.toLowerCase().includes(searchLower) ||
          item.supplier_code?.toLowerCase().includes(searchLower)
        );
      });
    },
  },
  watch: {
    warehouse: {
      handler(newVal) {
        if (newVal?.id) {
          this.fetchWarehouseStatus();
        }
      },
      immediate: true,
    },
    groceries: {
      handler() {
        // Update stock when groceries change
        if (this.warehouseStatus) {
          this.updateGroceriesWithStock();
        }
      },
      deep: true,
    },
  },
};
</script>
